/**
 * Demonstração de Resultados - Premium Board-Level Design
 * De Beers-Inspired Visual Authority System
 * ABC - Empresa ABC
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, BarChart3, Activity, Percent, FileText,
  ArrowUpRight, ArrowDownRight, Minus, Target, CheckCircle, AlertTriangle, Info
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Cell, LabelList
} from 'recharts';
import { mockData } from '../../data/mockData';
import ExecutiveSummarySection from '../../components/ExecutiveSummarySection';
import { 
  getCurrentPeriodLabel, 
  getPreviousPeriodLabel, 
  getBudgetLabel,
  getShortBudgetLabel,
  getTitleSuffix,
  getComparisonSubtitle,
  replaceYearReferences 
} from '../../utils/timeLabels';
// Import REAL financial data
import { 
  realIncomeStatementData,
  budgetIncomeStatementData,
  toMillionsUSD 
} from '../../data/mock/realFinancialData';

// ============ PREMIUM DESIGN TOKENS ============
const colors = {
  text: {
    primary: '#0E1633',
    secondary: '#425070',
    tertiary: '#7A859D',
    inverse: '#FFFFFF',
  },
  bg: {
    page: '#F5F7FB',
    card: '#FFFFFF',
    dark: '#0D1538',
    surfaceAlt: '#F8FAFC',
  },
  border: {
    default: '#E3E8F1',
    soft: '#EEF2F7',
  },
  status: {
    positive: '#0C8A4B',
    positiveBg: '#EAF7EF',
    warning: '#A07A18',
    warningBg: '#F8F1DD',
    negative: '#C33D52',
    negativeBg: '#FBECEF',
  },
  accent: {
    gold: '#B89A3C',
    goldBg: '#FBF8F0',
  },
  chart: {
    revenue: '#0D1538',
    cost: '#C33D52',
    ebitda: '#B89A3C',
    netIncome: '#0C8A4B',
  }
};

const typography = {
  pageTitle: 'font-serif text-[32px] leading-[40px] font-semibold tracking-[-0.3px]',
  sectionTitle: 'font-serif text-[20px] leading-[26px] font-semibold tracking-[-0.2px]',
  cardLabel: 'font-sans text-[11px] leading-[14px] font-semibold tracking-[0.4px]',
  kpiValue: 'font-sans text-[26px] leading-[30px] font-semibold tracking-[-0.5px]',
  bodyText: 'font-sans text-[14px] leading-[22px] font-normal',
  smallText: 'font-sans text-[12px] leading-[16px] font-normal',
  microText: 'font-sans text-[10px] leading-[14px] font-medium tracking-[0.3px] uppercase',
};

// ============ HELPER FUNCTIONS ============
const formatM = (value) => {
  if (value === null || value === undefined) return '-';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  return num.toFixed(1);
};

const getStatusColor = (value, inverse = false) => {
  const isPositive = inverse ? value <= 0 : value >= 0;
  return isPositive ? colors.status.positive : colors.status.negative;
};

const getStatusBg = (value, inverse = false) => {
  const isPositive = inverse ? value <= 0 : value >= 0;
  return isPositive ? colors.status.positiveBg : colors.status.negativeBg;
};

// ============ COMPACT KPI TOOLTIP ============
const KPITooltip = ({ isVisible, title, definition, relevance, currentReading }) => {
  if (!isVisible) return null;
  
  return (
    <div 
      className="absolute z-50 w-56 bg-[#0A192F] border border-slate-700/50 rounded-lg shadow-xl p-3 top-full mt-2 left-1/2 -translate-x-1/2"
      style={{ boxShadow: '0 10px 25px rgba(10, 25, 47, 0.35)' }}
    >
      <div className="absolute left-1/2 -translate-x-1/2 w-2 h-2 bg-[#0A192F] border-slate-700/50 transform rotate-45 -top-1 border-t border-l" />
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 pb-1.5 border-b border-slate-700/40">
          <div className="w-0.5 h-3 bg-gold-500 rounded-full"></div>
          <h4 className="text-gold-400 font-semibold text-xs uppercase tracking-wider">{title}</h4>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Definicao</p>
          <p className="text-white text-[11px] leading-snug">{definition}</p>
        </div>
        <div>
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Relevancia</p>
          <p className="text-slate-300 text-[11px] leading-snug">{relevance}</p>
        </div>
        <div className="bg-slate-800/40 rounded px-2 py-1.5 border border-slate-700/20">
          <p className="text-slate-500 text-[10px] font-semibold uppercase tracking-wider mb-0.5">Leitura Actual</p>
          <p className="text-emerald-400 text-[11px] leading-snug">{currentReading}</p>
        </div>
      </div>
    </div>
  );
};

// ============ PREMIUM KPI CARD WITH TOOLTIP ============
const KPICard = ({ title, value, unit, vsBudget, vsYoY, changeUnit = '%', icon: Icon, inverse = false, tooltipData, budgetLabel = 'vs Orç.', yoyLabel = 'YoY' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const budgetColor = getStatusColor(vsBudget, inverse);
  const budgetBg = getStatusBg(vsBudget, inverse);
  const yoyColor = getStatusColor(vsYoY, inverse);
  
  // Determine trend indicator based on YoY
  const numYoY = parseFloat(vsYoY);
  const isPositiveOutcome = inverse ? numYoY < 0 : numYoY > 0;
  const trendBg = isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg;
  const trendColor = isPositiveOutcome ? colors.status.positive : colors.status.negative;
  const TrendIcon = numYoY > 0 ? ArrowUpRight : numYoY < 0 ? ArrowDownRight : Minus;
  
  return (
    <div 
      className={`rounded-[14px] p-5 transition-all hover:shadow-md relative overflow-visible ${tooltipData ? 'cursor-help' : ''}`}
      style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
      onMouseEnter={() => tooltipData && setShowTooltip(true)}
      onMouseLeave={() => tooltipData && setShowTooltip(false)}
    >
      {/* Trend Arrow Indicator */}
      <div 
        className="absolute top-4 right-3 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ backgroundColor: trendBg }}
      >
        <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
      </div>
      
      {/* Info Icon */}
      {tooltipData && (
        <div className="absolute top-4 right-12">
          <Info className="w-4 h-4 text-slate-300 hover:text-gold-500 transition-colors" />
        </div>
      )}
      
      <div className="flex items-start justify-between mb-3">
        <div 
          className="w-9 h-9 rounded-[10px] flex items-center justify-center"
          style={{ backgroundColor: colors.bg.surfaceAlt }}
        >
          <Icon className="w-4.5 h-4.5" style={{ color: colors.text.tertiary }} />
        </div>
      </div>
      <p className={typography.cardLabel} style={{ color: colors.text.tertiary, marginBottom: '6px' }}>{title}</p>
      <div className="flex items-baseline gap-1.5">
        <span className={typography.kpiValue} style={{ color: colors.text.primary }}>{value}</span>
        <span className={typography.smallText} style={{ color: colors.text.tertiary }}>{unit}</span>
      </div>
      <div className="flex items-center gap-3 mt-3">
        <span 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
          style={{ backgroundColor: budgetBg, color: budgetColor }}
        >
          {parseFloat(vsBudget) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {vsBudget > 0 ? '+' : ''}{vsBudget}{changeUnit} {budgetLabel}
        </span>
        <span 
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-semibold"
          style={{ 
            backgroundColor: isPositiveOutcome ? colors.status.positiveBg : colors.status.negativeBg,
            color: yoyColor 
          }}
        >
          {parseFloat(vsYoY) > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {vsYoY > 0 ? '+' : ''}{vsYoY}{changeUnit} {yoyLabel}
        </span>
      </div>
      
      {/* Compact Tooltip */}
      {tooltipData && (
        <KPITooltip 
          isVisible={showTooltip}
          title={tooltipData.title}
          definition={tooltipData.definition}
          relevance={tooltipData.relevance}
          currentReading={tooltipData.currentReading}
        />
      )}
    </div>
  );
};

// ============ MAIN COMPONENT ============
const IncomeStatementMain = ({ year, language, setActiveSection, flowMap }) => {
  const yearlyData = mockData.finance.yearly[year] || mockData.finance.yearly[2025];
  const prevYearData = mockData.finance.yearly[year - 1] || mockData.finance.yearly[2024];
  const budgetData = mockData.finance.realVsBudget[year] || mockData.finance.realVsBudget[2025];

  // Dynamic time labels based on year (quarterly format for 2026)
  const currentPeriod = getCurrentPeriodLabel(year, language);
  const previousPeriod = getPreviousPeriodLabel(year, language);
  const budgetLabelFull = getBudgetLabel(year, language);
  const budgetLabelShort = getShortBudgetLabel(year, language);
  const titleSuffix = getTitleSuffix(year, language);
  const comparisonSubtitle = getComparisonSubtitle(year, language);
  const vsOrcLabel = year === 2026 ? 'vs Orç. 1T' : 'vs Orç.';
  const yoyLabel = year === 2026 ? `vs ${previousPeriod}` : 'YoY';

  // ============ REAL DATA EXTRACTION ============
  // Helper to get data for current/prior year (handles 2026 quarterly structure)
  const getRealData = (yr) => {
    if (yr === 2026) return realIncomeStatementData[2026].Q1; // Use Q1 2026 data for "actual"
    return realIncomeStatementData[yr] || realIncomeStatementData[2024];
  };
  
  // For "Real" columns: apply adjustment factors since we only have budget data
  // - 2025: Data is now REAL (no factor needed) - Updated to actual values
  // - 2026: "Real" column shows Q1 Budget with -5% (approximation of executed values)
  // - 2024: No factor needed (actual data exists)
  const getActualFactor = (yr) => {
    if (yr === 2025) return 1.00; // 2025 is now REAL data (no adjustment)
    if (yr === 2026) return 0.95; // Q1 2026 Budget - 5%
    return 1; // 2024 uses actual data
  };
  const actualFactor = getActualFactor(year);
  
  const currentRealData = getRealData(year);
  const priorRealData = getRealData(year - 1);
  
  // For budget comparison:
  // - 2024: no budget exists, use actual
  // - 2025: use 2025 budget from budgetIncomeStatementData
  // - 2026: use Q1 2026 Budget from Excel (realIncomeStatementData[2026].Q1)
  const getBudgetData = (yr) => {
    if (yr === 2024) return realIncomeStatementData[2024];
    if (yr === 2025) return budgetIncomeStatementData[2025]; // Use separate budget data
    if (yr === 2026) return realIncomeStatementData[2026].Q1; // Use Q1 2026 Budget
    return realIncomeStatementData[yr] || realIncomeStatementData[2025];
  };
  const budgetRealData = getBudgetData(year);
  
  // For 2026 quarterly view: prior year column (1T25) should show 25% of annual 2025 values
  // This is an approximation since we don't have actual Q1 2025 data
  const priorQuarterFactor = year === 2026 ? 0.25 : 1;

  // ============ REAL VALUES FROM SOURCE (in M USD) ============
  // Proveitos Operacionais (Operating Income)
  // For 2026: apply 95% factor to Q1 Budget to simulate "actual" values
  const vendas = toMillionsUSD(currentRealData.vendas) * actualFactor;
  const prestacoesServicos = toMillionsUSD(currentRealData.prestacoesServicos) * actualFactor;
  const outrosProveitos = toMillionsUSD(currentRealData.outrosProveitos) * actualFactor;
  const variacaoPAePVF = toMillionsUSD(currentRealData.variacaoPAePVF) * actualFactor;
  const totalProveitos = toMillionsUSD(currentRealData.totalProveitos) * actualFactor;
  
  // Prior year values (apply 25% factor for 2026 quarterly view to approximate Q1 2025)
  const vendasPrior = toMillionsUSD(priorRealData.vendas) * priorQuarterFactor;
  const prestacoesServicosPrior = toMillionsUSD(priorRealData.prestacoesServicos) * priorQuarterFactor;
  const outrosProveitosPrior = toMillionsUSD(priorRealData.outrosProveitos) * priorQuarterFactor;
  const variacaoPAePVFPrior = toMillionsUSD(priorRealData.variacaoPAePVF) * priorQuarterFactor;
  const totalProveitosPrior = toMillionsUSD(priorRealData.totalProveitos) * priorQuarterFactor;
  
  // Budget values (same as actual for 2025/2026 since only budget exists)
  const vendasBudget = toMillionsUSD(budgetRealData.vendas);
  const prestacoesServicosBudget = toMillionsUSD(budgetRealData.prestacoesServicos);
  const outrosProveitosBudget = toMillionsUSD(budgetRealData.outrosProveitos);
  const variacaoPAePVFBudget = toMillionsUSD(budgetRealData.variacaoPAePVF);
  const totalProveitosBudget = toMillionsUSD(budgetRealData.totalProveitos);
  
  // Custos Operacionais (Operating Costs) - in M USD as positive values for display
  // For 2026: apply 95% factor to Q1 Budget to simulate "actual" values
  const cmvmc = Math.abs(toMillionsUSD(currentRealData.cmvmc)) * actualFactor;
  const personnel = Math.abs(toMillionsUSD(currentRealData.custosPessoal)) * actualFactor;
  const subcontracts = Math.abs(toMillionsUSD(currentRealData.subcontratos)) * actualFactor;
  const gna = Math.abs(toMillionsUSD(currentRealData.geraisAdministrativos)) * actualFactor;
  const commercial = Math.abs(toMillionsUSD(currentRealData.custosComercializacao)) * actualFactor;
  const totalOpex = cmvmc + personnel + subcontracts + gna + commercial;
  
  // Prior year costs (apply 25% factor for 2026 quarterly view)
  const cmvmcPrior = Math.abs(toMillionsUSD(priorRealData.cmvmc)) * priorQuarterFactor;
  const personnelPrior = Math.abs(toMillionsUSD(priorRealData.custosPessoal)) * priorQuarterFactor;
  const subcontractsPrior = Math.abs(toMillionsUSD(priorRealData.subcontratos)) * priorQuarterFactor;
  const gnaPrior = Math.abs(toMillionsUSD(priorRealData.geraisAdministrativos)) * priorQuarterFactor;
  const commercialPrior = Math.abs(toMillionsUSD(priorRealData.custosComercializacao)) * priorQuarterFactor;
  const prevOpex = cmvmcPrior + personnelPrior + subcontractsPrior + gnaPrior + commercialPrior;
  
  const cmvmcBudget = Math.abs(toMillionsUSD(budgetRealData.cmvmc));
  const personnelBudget = Math.abs(toMillionsUSD(budgetRealData.custosPessoal));
  const subcontractsBudget = Math.abs(toMillionsUSD(budgetRealData.subcontratos));
  const gnaBudget = Math.abs(toMillionsUSD(budgetRealData.geraisAdministrativos));
  const commercialBudget = Math.abs(toMillionsUSD(budgetRealData.custosComercializacao));
  const budgetOpex = cmvmcBudget + personnelBudget + subcontractsBudget + gnaBudget + commercialBudget;
  
  // Results (EBITDA and below)
  // For 2026: apply 95% factor to Q1 Budget to simulate "actual" values
  // Note: Margins (%) are recalculated based on actual values, not multiplied
  const ebitda = toMillionsUSD(currentRealData.ebitda) * actualFactor;
  // Recalculate margin for 2026 based on actual factor-adjusted values
  const ebitdaMargin = year === 2026 
    ? parseFloat(((ebitda / totalProveitos) * 100).toFixed(2))
    : parseFloat((currentRealData.margemEbitda * 100).toFixed(2));
  const depreciation = Math.abs(toMillionsUSD(currentRealData.amortizacoes)) * actualFactor;
  const ebit = toMillionsUSD(currentRealData.ebit) * actualFactor;
  const nonOperating = toMillionsUSD(currentRealData.resultadosNaoOperacionais) * actualFactor;
  const financialResults = toMillionsUSD(currentRealData.resultadosFinanceiros) * actualFactor;
  const profitBeforeTax = toMillionsUSD(currentRealData.resultadoAntesImposto) * actualFactor;
  const tax = Math.abs(toMillionsUSD(currentRealData.impostoIndustrial)) * actualFactor;
  const netIncome = toMillionsUSD(currentRealData.resultadoLiquido) * actualFactor;
  
  // Prior year results (apply 25% factor for 2026 quarterly view)
  // Note: Margins (%) don't need the factor as they are ratios
  const ebitdaPrior = toMillionsUSD(priorRealData.ebitda) * priorQuarterFactor;
  const ebitdaMarginPrior = parseFloat((priorRealData.margemEbitda * 100).toFixed(2)); // Margin stays same
  const depreciationPrior = Math.abs(toMillionsUSD(priorRealData.amortizacoes)) * priorQuarterFactor;
  const ebitPrior = toMillionsUSD(priorRealData.ebit) * priorQuarterFactor;
  const nonOperatingPrior = toMillionsUSD(priorRealData.resultadosNaoOperacionais) * priorQuarterFactor;
  const financialResultsPrior = toMillionsUSD(priorRealData.resultadosFinanceiros) * priorQuarterFactor;
  const profitBeforeTaxPrior = toMillionsUSD(priorRealData.resultadoAntesImposto) * priorQuarterFactor;
  const taxPrior = Math.abs(toMillionsUSD(priorRealData.impostoIndustrial)) * priorQuarterFactor;
  const prevNetIncome = toMillionsUSD(priorRealData.resultadoLiquido) * priorQuarterFactor;
  
  // Budget results
  const budgetEbitda = toMillionsUSD(budgetRealData.ebitda);
  const budgetEbitdaMargin = parseFloat((budgetRealData.margemEbitda * 100).toFixed(2));
  const depreciationBudget = Math.abs(toMillionsUSD(budgetRealData.amortizacoes));
  const ebitBudget = toMillionsUSD(budgetRealData.ebit);
  const nonOperatingBudget = toMillionsUSD(budgetRealData.resultadosNaoOperacionais);
  const financialResultsBudget = toMillionsUSD(budgetRealData.resultadosFinanceiros);
  const profitBeforeTaxBudget = toMillionsUSD(budgetRealData.resultadoAntesImposto);
  const taxBudget = Math.abs(toMillionsUSD(budgetRealData.impostoIndustrial));
  const budgetNetIncome = toMillionsUSD(budgetRealData.resultadoLiquido);
  
  // Budget revenue for KPI cards (use yearlyData which already imports real data)
  const budgetRevenue = totalProveitosBudget;
  
  // Variance calculations (using real data)
  const revenueVsBudget = budgetRevenue !== 0 ? ((totalProveitos - budgetRevenue) / Math.abs(budgetRevenue) * 100).toFixed(0) : '0';
  const opexVsBudget = budgetOpex !== 0 ? ((totalOpex - budgetOpex) / Math.abs(budgetOpex) * 100).toFixed(0) : '0';
  const ebitdaVsBudget = budgetEbitda !== 0 ? ((ebitda - budgetEbitda) / Math.abs(budgetEbitda) * 100).toFixed(0) : '0';
  const ebitdaMarginVsBudget = (ebitdaMargin - budgetEbitdaMargin).toFixed(1);
  const netIncomeVsBudget = budgetNetIncome !== 0 ? ((netIncome - budgetNetIncome) / Math.abs(budgetNetIncome) * 100).toFixed(0) : '0';
  
  // YoY calculations (using real data)
  const revenueYoY = totalProveitosPrior !== 0 ? ((totalProveitos / totalProveitosPrior - 1) * 100).toFixed(0) : '0';
  const opexYoY = prevOpex !== 0 ? ((totalOpex / prevOpex - 1) * 100).toFixed(0) : '0';
  const ebitdaYoY = ebitdaPrior !== 0 ? ((ebitda / ebitdaPrior - 1) * 100).toFixed(0) : '0';
  const ebitdaMarginYoY = (ebitdaMargin - ebitdaMarginPrior).toFixed(1);
  const netIncomeYoY = prevNetIncome !== 0 ? ((netIncome / prevNetIncome - 1) * 100).toFixed(0) : '0';

  // Waterfall data - using REAL DATA with Variação PA e PVF
  const waterfallData = [
    { name: language === 'pt' ? 'Receitas' : 'Revenue', value: totalProveitos, isStart: true },
    { name: language === 'pt' ? 'Var. PA/PVF' : 'WIP Var.', value: variacaoPAePVF, isNegative: variacaoPAePVF < 0 },
    { name: 'CMVMC', value: -cmvmc, isNegative: true },
    { name: language === 'pt' ? 'Pessoal' : 'Personnel', value: -personnel, isNegative: true },
    { name: language === 'pt' ? 'Subcontr.' : 'Subcontr.', value: -subcontracts, isNegative: true },
    { name: language === 'pt' ? 'G&A' : 'G&A', value: -gna, isNegative: true },
    { name: language === 'pt' ? 'Comerc.' : 'Selling', value: -commercial, isNegative: true },
    { name: 'EBITDA', value: ebitda, isSubtotal: true },
    { name: 'D&A', value: -depreciation, isNegative: true },
    { name: language === 'pt' ? 'Não Op.' : 'Non-Op.', value: nonOperating, isNegative: nonOperating < 0 },
    { name: language === 'pt' ? 'Financ.' : 'Financial', value: financialResults, isNegative: financialResults < 0 },
    { name: language === 'pt' ? 'Imposto' : 'Tax', value: -tax, isNegative: true },
    { name: language === 'pt' ? 'Res. Líq.' : 'Net Inc.', value: netIncome, isTotal: true },
  ];

  // Calculate waterfall bar positions
  const waterfallBars = [];
  let runningTotal = 0;
  
  waterfallData.forEach((item) => {
    if (item.isStart) {
      waterfallBars.push({ ...item, start: 0, end: item.value, displayValue: item.value });
      runningTotal = item.value;
    } else if (item.isTotal || item.isSubtotal) {
      const barValue = item.isSubtotal ? ebitda : netIncome;
      waterfallBars.push({ ...item, start: 0, end: barValue, displayValue: barValue });
      runningTotal = barValue;
    } else {
      const newTotal = runningTotal + item.value;
      if (item.value < 0) {
        waterfallBars.push({ ...item, start: newTotal, end: runningTotal, displayValue: item.value });
      } else {
        waterfallBars.push({ ...item, start: runningTotal, end: newTotal, displayValue: item.value });
      }
      runningTotal = newTotal;
    }
  });

  // Main P&L lines for deviation panel - using REAL DATA
  const deviationData = [
    { label: { pt: 'Receitas', en: 'Revenue' }, real2025: totalProveitos, budget2025: budgetRevenue, real2024: totalProveitosPrior },
    { label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, real2025: totalOpex, budget2025: budgetOpex, real2024: prevOpex, inverse: true },
    { label: { pt: 'EBITDA', en: 'EBITDA' }, real2025: ebitda, budget2025: budgetEbitda, real2024: ebitdaPrior, highlight: true },
    { label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, real2025: ebitdaMargin, budget2025: budgetEbitdaMargin, real2024: ebitdaMarginPrior, isPercent: true },
    { label: { pt: 'Resultado Líquido', en: 'Net Income' }, real2025: netIncome, budget2025: budgetNetIncome, real2024: prevNetIncome, highlight: true },
  ];

  // Full Income Statement data - UPDATED WITH REAL DATA and Variação nos PA e PVF
  const incomeStatementRows = [
    { type: 'header', label: { pt: 'PROVEITOS OPERACIONAIS', en: 'OPERATING INCOME' } },
    { type: 'row', label: { pt: 'Vendas', en: 'Sales' }, current: vendas, budget: vendasBudget, prior: vendasPrior },
    { type: 'row', label: { pt: 'Prestações de Serviços', en: 'Services' }, current: prestacoesServicos, budget: prestacoesServicosBudget, prior: prestacoesServicosPrior },
    { type: 'row', label: { pt: 'Outros Proveitos', en: 'Other Income' }, current: outrosProveitos, budget: outrosProveitosBudget, prior: outrosProveitosPrior },
    { type: 'row', label: { pt: 'Variação nos PA e PVF', en: 'WIP & Finished Goods Variation' }, current: variacaoPAePVF, budget: variacaoPAePVFBudget, prior: variacaoPAePVFPrior, isVariation: true },
    { type: 'subtotal', label: { pt: 'Total Proveitos', en: 'Total Income' }, current: totalProveitos, budget: totalProveitosBudget, prior: totalProveitosPrior },
    { type: 'header', label: { pt: 'CUSTOS OPERACIONAIS', en: 'OPERATING COSTS' } },
    { type: 'row', label: { pt: 'CMVMC', en: 'COGS' }, current: cmvmc, budget: cmvmcBudget, prior: cmvmcPrior, isNegative: true },
    { type: 'row', label: { pt: 'Custos com Pessoal', en: 'Personnel' }, current: personnel, budget: personnelBudget, prior: personnelPrior, isNegative: true },
    { type: 'row', label: { pt: 'Subcontratos', en: 'Subcontracts' }, current: subcontracts, budget: subcontractsBudget, prior: subcontractsPrior, isNegative: true },
    { type: 'row', label: { pt: 'Gerais e Administrativos', en: 'General & Administrative' }, current: gna, budget: gnaBudget, prior: gnaPrior, isNegative: true },
    { type: 'row', label: { pt: 'Custos de Comercialização', en: 'Commercial Costs' }, current: commercial, budget: commercialBudget, prior: commercialPrior, isNegative: true },
    { type: 'subtotal', label: { pt: 'Total Custos Op.', en: 'Total Op. Costs' }, current: totalOpex, budget: budgetOpex, prior: prevOpex, isNegative: true },
    { type: 'highlight', label: { pt: 'EBITDA', en: 'EBITDA' }, current: ebitda, budget: budgetEbitda, prior: ebitdaPrior },
    { type: 'margin', label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, current: ebitdaMargin, budget: budgetEbitdaMargin, prior: ebitdaMarginPrior, isPercent: true },
    { type: 'header', label: { pt: 'EBITDA AO RESULTADO LÍQUIDO', en: 'EBITDA TO NET INCOME' } },
    { type: 'row', label: { pt: 'Amortizações', en: 'Amortization' }, current: depreciation, budget: depreciationBudget, prior: depreciationPrior, isNegative: true },
    { type: 'subtotal', label: { pt: 'EBIT', en: 'EBIT' }, current: ebit, budget: ebitBudget, prior: ebitPrior },
    { type: 'row', label: { pt: 'Resultados Não Operacionais', en: 'Non-Operating Results' }, current: nonOperating, budget: nonOperatingBudget, prior: nonOperatingPrior, isNegative: nonOperating < 0 },
    { type: 'row', label: { pt: 'Resultados Financeiros', en: 'Financial Results' }, current: financialResults, budget: financialResultsBudget, prior: financialResultsPrior, isNegative: financialResults < 0 },
    { type: 'subtotal', label: { pt: 'Resultado Antes de Imposto', en: 'Profit Before Tax' }, current: profitBeforeTax, budget: profitBeforeTaxBudget, prior: profitBeforeTaxPrior },
    { type: 'row', label: { pt: 'Imposto Industrial', en: 'Income Tax' }, current: tax, budget: taxBudget, prior: taxPrior, isNegative: true },
    { type: 'highlight', label: { pt: 'RESULTADO LÍQUIDO', en: 'NET INCOME' }, current: netIncome, budget: budgetNetIncome, prior: prevNetIncome },
  ];

  // Key messages - CAPEX style with titles and icons
  const keyMessages = {
    pt: [
      { 
        title: 'RENTABILIDADE', 
        text: 'EBITDA acima do orçamento em +' + ebitdaVsBudget + '%, impulsionado por receitas sólidas e controlo de custos operacionais.',
        icon: Target,
        color: 'amber'
      },
      { 
        title: 'CUSTOS', 
        text: 'Custos operacionais controlados, ' + opexVsBudget + '% face ao orçamento, demonstrando disciplina na gestão de despesas.',
        icon: CheckCircle,
        color: 'emerald'
      },
      { 
        title: 'MARGEM', 
        text: 'Margem EBITDA de ' + ebitdaMargin.toFixed(1) + '%, ' + (ebitdaMarginYoY > 0 ? '+' : '') + ebitdaMarginYoY + 'p.p. vs ' + previousPeriod + ', reflectindo melhoria estrutural.',
        icon: AlertTriangle,
        color: 'blue'
      },
    ],
    en: [
      { 
        title: 'PROFITABILITY', 
        text: 'EBITDA above budget by +' + ebitdaVsBudget + '%, driven by solid revenue and operational cost control.',
        icon: Target,
        color: 'amber'
      },
      { 
        title: 'COSTS', 
        text: 'Operating costs controlled, ' + opexVsBudget + '% budget, demonstrating expense management discipline.',
        icon: CheckCircle,
        color: 'emerald'
      },
      { 
        title: 'MARGIN', 
        text: 'EBITDA margin at ' + ebitdaMargin.toFixed(1) + '%, ' + (ebitdaMarginYoY > 0 ? '+' : '') + ebitdaMarginYoY + 'p.p. vs ' + previousPeriod + ', reflecting structural improvement.',
        icon: AlertTriangle,
        color: 'blue'
      },
    ],
  };

  return (
    <div className="space-y-8" style={{ backgroundColor: colors.bg.page }} data-testid="income-statement-main">
      
      {/* ============ PAGE HEADER ============ */}
      <div>
        <h1 
          className="text-[38px] leading-[44px] font-semibold tracking-[-0.3px] mb-2"
          style={{ color: colors.text.primary, fontFamily: '"Source Serif 4", serif' }}
        >
          {language === 'pt' ? 'Demonstração de Resultados' : 'Income Statement'} | {titleSuffix}
        </h1>
        <p 
          className="text-[15px] leading-[22px]"
          style={{ color: colors.text.secondary }}
        >
          {comparisonSubtitle}
        </p>
        {/* Finance Flow Map - appears below title, above key messages */}
        {flowMap && <div className="mt-4">{flowMap}</div>}
      </div>

      {/* ============ KEY MESSAGES BAND - CAPEX Style ============ */}
      <div className="bg-gradient-to-r from-[#0A192F] to-[#1a2744] rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-1 h-5 bg-gold-500 rounded-full"></div>
          <h3 className="text-gold-400 font-semibold text-sm uppercase tracking-wider">
            {language === 'pt' ? 'Mensagens-Chave' : 'Key Messages'}
          </h3>
        </div>
        <div className="grid grid-cols-3 gap-5">
          {keyMessages[language].map((msg, idx) => {
            const IconComponent = msg.icon;
            const colorClasses = {
              amber: { bg: 'bg-amber-500/30', icon: 'text-amber-400', title: 'text-gold-400' },
              emerald: { bg: 'bg-emerald-500/30', icon: 'text-emerald-400', title: 'text-emerald-400' },
              blue: { bg: 'bg-blue-500/30', icon: 'text-blue-400', title: 'text-blue-400' }
            };
            const colors = colorClasses[msg.color];
            return (
              <div 
                key={idx} 
                className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-4 transition-all duration-300 hover:scale-[1.02]"
              >
                <div className="flex items-start gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                    <IconComponent className={`w-5 h-5 ${colors.icon}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className={`font-semibold text-sm mb-1.5 leading-tight ${colors.title}`}>{msg.title}</h4>
                    <p className="text-slate-400 text-xs leading-relaxed">{msg.text}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ============ KPI SCORECARD ============ */}
      <div className="grid grid-cols-5 gap-5">
        <KPICard
          title={language === 'pt' ? 'Receitas' : 'Revenue'}
          value={formatM(totalProveitos)}
          unit="M USD"
          icon={DollarSign}
          vsBudget={parseInt(revenueVsBudget)}
          vsYoY={parseInt(revenueYoY)}
          budgetLabel={vsOrcLabel}
          yoyLabel={yoyLabel}
          tooltipData={{
            title: language === 'pt' ? 'Receitas' : 'Revenue',
            definition: language === 'pt' ? 'Total de vendas de diamantes e outros proveitos operacionais.' : 'Total diamond sales and other operating income.',
            relevance: language === 'pt' ? 'Principal indicador de volume de negócio e capacidade comercial.' : 'Main indicator of business volume and commercial capacity.',
            currentReading: language === 'pt' 
              ? `${formatM(totalProveitos)}M USD representa crescimento de ${revenueYoY}% face a ${previousPeriod}.`
              : `${formatM(totalProveitos)}M USD represents ${revenueYoY}% growth vs ${previousPeriod}.`
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Custos Operacionais' : 'Operating Costs'}
          value={formatM(totalOpex)}
          unit="M USD"
          icon={BarChart3}
          vsBudget={parseInt(opexVsBudget)}
          vsYoY={parseInt(opexYoY)}
          inverse={true}
          budgetLabel={vsOrcLabel}
          yoyLabel={yoyLabel}
          tooltipData={{
            title: language === 'pt' ? 'Custos Operacionais' : 'Operating Costs',
            definition: language === 'pt' ? 'Soma de CMVMC, pessoal, subcontratos e G&A.' : 'Sum of COGS, personnel, subcontracts and G&A.',
            relevance: language === 'pt' ? 'Monitoriza eficiência operacional e disciplina de custos.' : 'Monitors operational efficiency and cost discipline.',
            currentReading: language === 'pt' 
              ? `${formatM(totalOpex)}M USD, ${opexVsBudget}% vs orçamento indica bom controlo de custos.`
              : `${formatM(totalOpex)}M USD, ${opexVsBudget}% budget indicates good cost control.`
          }}
        />
        <KPICard
          title="EBITDA"
          value={formatM(ebitda)}
          unit="M USD"
          icon={Activity}
          vsBudget={parseInt(ebitdaVsBudget)}
          vsYoY={parseInt(ebitdaYoY)}
          budgetLabel={vsOrcLabel}
          yoyLabel={yoyLabel}
          tooltipData={{
            title: 'EBITDA',
            definition: language === 'pt' ? 'Resultado antes de juros, impostos, depreciações e amortizações.' : 'Earnings before interest, taxes, depreciation and amortization.',
            relevance: language === 'pt' ? 'Proxy de geração de caixa operacional, independente de estrutura financeira.' : 'Proxy for operational cash generation, independent of financial structure.',
            currentReading: language === 'pt' 
              ? `${formatM(ebitda)}M USD, +${ebitdaVsBudget}% acima do orçamento reflecte performance sólida.`
              : `${formatM(ebitda)}M USD, +${ebitdaVsBudget}% above budget reflects solid performance.`
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin'}
          value={ebitdaMargin.toFixed(1)}
          unit="%"
          icon={Percent}
          vsBudget={parseFloat(ebitdaMarginVsBudget)}
          vsYoY={parseFloat(ebitdaMarginYoY)}
          changeUnit="p.p."
          budgetLabel={vsOrcLabel}
          yoyLabel={yoyLabel}
          tooltipData={{
            title: language === 'pt' ? 'Margem EBITDA' : 'EBITDA Margin',
            definition: language === 'pt' ? 'EBITDA dividido por receitas, em percentagem.' : 'EBITDA divided by revenue, as percentage.',
            relevance: language === 'pt' ? 'Mede rentabilidade operacional por cada USD de receita.' : 'Measures operational profitability per USD of revenue.',
            currentReading: language === 'pt' 
              ? `${ebitdaMargin.toFixed(1)}% é uma margem robusta para o sector mineiro.`
              : `${ebitdaMargin.toFixed(1)}% is a robust margin for the mining sector.`
          }}
        />
        <KPICard
          title={language === 'pt' ? 'Resultado Líquido' : 'Net Income'}
          value={formatM(netIncome)}
          unit="M USD"
          icon={TrendingUp}
          vsBudget={parseInt(netIncomeVsBudget)}
          vsYoY={parseInt(netIncomeYoY)}
          budgetLabel={vsOrcLabel}
          yoyLabel={yoyLabel}
          tooltipData={{
            title: language === 'pt' ? 'Resultado Líquido' : 'Net Income',
            definition: language === 'pt' ? 'Lucro final após todos os custos, juros e impostos.' : 'Final profit after all costs, interest and taxes.',
            relevance: language === 'pt' ? 'Representa o valor disponível para reinvestimento ou distribuição.' : 'Represents value available for reinvestment or distribution.',
            currentReading: language === 'pt' 
              ? `${formatM(netIncome)}M USD, crescimento de ${netIncomeYoY}% ${yoyLabel} demonstra melhoria sustentada.`
              : `${formatM(netIncome)}M USD, ${netIncomeYoY}% ${yoyLabel} growth demonstrates sustained improvement.`
          }}
        />
      </div>

      {/* ============ EXECUTIVE SUMMARY - CMVMC Style ============ */}
      <ExecutiveSummarySection
        title={language === 'pt' ? 'Sumário Executivo' : 'Executive Summary'}
        subtitle={language === 'pt' ? 'Demonstração de resultados' : 'Income statement'}
        language={language}
        maxHeight="500px"
        icon={Info}
        dataTestId="income-statement-executive-summary"
      >
        {/* Executive Overview */}
        <div className="mb-6">
          <p className="text-slate-700 leading-relaxed text-sm mb-4">
            {language === 'pt' 
              ? `Em ${currentPeriod}, a Demonstração de Resultados fechou com USD ${formatM(totalProveitos)}M de total de proveitos, o que representa um aumento de ${revenueYoY}% face a ${previousPeriod} e ${revenueVsBudget > 0 ? '+' : ''}${revenueVsBudget}% face ao ${budgetLabelFull}. A leitura principal desta linha é que a empresa entrou num patamar superior de actividade, ainda antes de capturar toda a alavancagem operacional prevista para o ciclo seguinte. O ponto relevante para a gerência não é apenas o crescimento dos proveitos, mas o facto de esse crescimento já ter sido suficiente para sustentar uma base operacional mais exigente e ainda assim preservar geração de margem.`
              : `In ${currentPeriod}, the Income Statement closed with USD ${formatM(totalProveitos)}M in total income, representing a ${revenueYoY}% increase vs ${previousPeriod} and ${revenueVsBudget > 0 ? '+' : ''}${revenueVsBudget}% vs ${budgetLabelFull}. The main reading is that the company entered a higher activity level, even before capturing all the operational leverage expected for the next cycle.`}
          </p>
        </div>

        {/* Driver Sections - Collapsible Cards */}
        <div className="space-y-4">
          {/* Estrutura de Custos e Qualidade da Margem */}
          <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-5 border border-blue-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Estrutura de Custos e Qualidade da Margem' : 'Cost Structure and Margin Quality'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Formação da margem operacional' : 'Operating margin formation'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A formação da margem em ${currentPeriod} foi determinada por quatro blocos principais: CMVMC de USD ${formatM(cmvmc)}M, custos de comercialização de USD ${formatM(commercial)}M, custos com pessoal de USD ${formatM(personnel)}M e gastos gerais e administrativos de USD ${formatM(gna)}M. Em percentagem dos proveitos, estas linhas corresponderam a ${Math.round(cmvmc/totalProveitos*100)}%, ${Math.round(commercial/totalProveitos*100)}%, ${Math.round(personnel/totalProveitos*100)}% e ${Math.round(gna/totalProveitos*100)}%, respectivamente.`
                : `Margin formation in ${currentPeriod} was determined by four main blocks: COGS of USD ${formatM(cmvmc)}M, commercial costs of USD ${formatM(commercial)}M, personnel costs of USD ${formatM(personnel)}M and G&A of USD ${formatM(gna)}M. As a percentage of income, these lines corresponded to ${Math.round(cmvmc/totalProveitos*100)}%, ${Math.round(commercial/totalProveitos*100)}%, ${Math.round(personnel/totalProveitos*100)}% and ${Math.round(gna/totalProveitos*100)}%, respectively.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Em termos de leitura executiva, a Demonstração de Resultados mostra que a principal pressão sobre a margem continuou a vir da combinação entre custo directo de produção e estrutura operacional, e não de uma única linha isolada. Face a ${previousPeriod}, a mensagem central é que o crescimento da base de proveitos já foi acompanhado por uma capacidade real de retenção de margem operacional.`
                : `In executive terms, the Income Statement shows that the main margin pressure continued to come from the combination of direct production cost and operational structure, not from a single isolated line. Compared to ${previousPeriod}, the central message is that income growth was already accompanied by real operating margin retention capacity.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-blue-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Decomposição dos Custos' : 'Cost Decomposition'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `CMVMC: ${formatM(cmvmc)}M USD (${Math.round(cmvmc/totalProveitos*100)}% dos proveitos)` : `COGS: ${formatM(cmvmc)}M USD (${Math.round(cmvmc/totalProveitos*100)}% of income)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Pessoal: ${formatM(personnel)}M USD (${Math.round(personnel/totalProveitos*100)}% dos proveitos)` : `Personnel: ${formatM(personnel)}M USD (${Math.round(personnel/totalProveitos*100)}% of income)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Gerais e Administrativos: ${formatM(gna)}M USD (${Math.round(gna/totalProveitos*100)}% dos proveitos)` : `G&A: ${formatM(gna)}M USD (${Math.round(gna/totalProveitos*100)}% of income)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500"></div>
                  {language === 'pt' ? `Comercialização: ${formatM(commercial)}M USD (${Math.round(commercial/totalProveitos*100)}% dos proveitos)` : `Commercial: ${formatM(commercial)}M USD (${Math.round(commercial/totalProveitos*100)}% of income)`}
                </li>
              </ul>
            </div>
          </div>

          {/* Rentabilidade Operacional e Resultado Líquido */}
          <div className="bg-gradient-to-r from-emerald-50 to-slate-50 rounded-xl p-5 border border-emerald-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Rentabilidade Operacional e Resultado Líquido' : 'Operating Profitability and Net Income'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Geração de valor em todos os níveis' : 'Value generation at all levels'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `O ano fechou com EBITDA de USD ${formatM(ebitda)}M, equivalente a ${ebitdaMargin.toFixed(1)}% dos proveitos, e com Resultado Operacional de USD ${formatM(ebit)}M, correspondente a ${Math.round(ebit/totalProveitos*100)}% dos proveitos. Abaixo da linha operacional, o Resultado Líquido foi de USD ${formatM(netIncome)}M, com margem líquida de ${Math.round(netIncome/totalProveitos*100)}%.`
                : `The year closed with EBITDA of USD ${formatM(ebitda)}M, equivalent to ${ebitdaMargin.toFixed(1)}% of income, and Operating Result of USD ${formatM(ebit)}M, corresponding to ${Math.round(ebit/totalProveitos*100)}% of income. Below the operating line, Net Income was USD ${formatM(netIncome)}M, with a net margin of ${Math.round(netIncome/totalProveitos*100)}%.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `Para a gerência, esta sequência confirma que a empresa voltou a gerar resultado em todos os níveis da DRE, mas ainda com espaço para melhorar a conversão da margem operacional em resultado líquido. Face ao ${budgetLabelFull}, a leitura deve concentrar-se em três pontos: EBITDA ${ebitdaVsBudget > 0 ? '+' : ''}${ebitdaVsBudget}%, Resultado Operacional e Resultado Líquido ${netIncomeVsBudget > 0 ? '+' : ''}${netIncomeVsBudget}%.`
                : `For management, this sequence confirms that the company returned to generating results at all P&L levels, but still with room to improve conversion of operating margin to net income. Against ${budgetLabelFull}, focus on three points: EBITDA ${ebitdaVsBudget > 0 ? '+' : ''}${ebitdaVsBudget}%, Operating Result and Net Income ${netIncomeVsBudget > 0 ? '+' : ''}${netIncomeVsBudget}%.`}
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-emerald-100">
              <p className="text-xs font-semibold text-slate-700 mb-2">{language === 'pt' ? 'Indicadores de Rentabilidade' : 'Profitability Indicators'}</p>
              <ul className="text-xs text-slate-600 space-y-1">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? `EBITDA: ${formatM(ebitda)}M USD (${ebitdaMargin.toFixed(1)}% margem)` : `EBITDA: ${formatM(ebitda)}M USD (${ebitdaMargin.toFixed(1)}% margin)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? `EBIT: ${formatM(ebit)}M USD (${Math.round(ebit/totalProveitos*100)}% margem)` : `EBIT: ${formatM(ebit)}M USD (${Math.round(ebit/totalProveitos*100)}% margin)`}
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  {language === 'pt' ? `Resultado Líquido: ${formatM(netIncome)}M USD (${Math.round(netIncome/totalProveitos*100)}% margem)` : `Net Income: ${formatM(netIncome)}M USD (${Math.round(netIncome/totalProveitos*100)}% margin)`}
                </li>
              </ul>
            </div>
          </div>

          {/* Síntese à Gerência */}
          <div className="bg-gradient-to-r from-amber-50 to-slate-50 rounded-xl p-5 border border-amber-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary text-sm">{language === 'pt' ? 'Síntese à Gerência' : 'Management Synthesis'}</h4>
                <p className="text-xs text-slate-500">{language === 'pt' ? 'Leitura consolidada do exercício' : 'Consolidated reading of the fiscal year'}</p>
              </div>
            </div>
            <p className="text-slate-600 text-xs leading-relaxed mb-3">
              {language === 'pt' 
                ? `A Demonstração de Resultados de ${currentPeriod} mostra uma empresa que já conseguiu estabilizar a geração de resultado operacional. Os proveitos cresceram para USD ${formatM(totalProveitos)}M, o EBITDA atingiu USD ${formatM(ebitda)}M e o resultado líquido fechou em USD ${formatM(netIncome)}M. A leitura de gerência é que ${currentPeriod} não foi ainda um período de plena captura económica da capacidade instalada, mas já foi um período em que a formação da margem se tornou mais robusta.`
                : `The ${currentPeriod} Income Statement shows a company that has already managed to stabilize operating result generation. Income grew to USD ${formatM(totalProveitos)}M, EBITDA reached USD ${formatM(ebitda)}M and net income closed at USD ${formatM(netIncome)}M. Management reading is that ${currentPeriod} was not yet a period of full economic capture of installed capacity, but already a period where margin formation became more robust.`}
            </p>
            <p className="text-slate-600 text-xs leading-relaxed">
              {language === 'pt' 
                ? `Na comparação com o ${budgetLabelFull}, os proveitos ficaram ${revenueVsBudget > 0 ? 'acima' : 'abaixo'} do plano (${revenueVsBudget > 0 ? '+' : ''}${revenueVsBudget}%) e o EBITDA teve desvio de ${ebitdaVsBudget > 0 ? '+' : ''}${ebitdaVsBudget}%. A explicação deve ser procurada na combinação entre volume, preço, absorção da estrutura e intensidade dos custos abaixo da margem operacional.`
                : `Compared to ${budgetLabelFull}, income was ${revenueVsBudget > 0 ? 'above' : 'below'} plan (${revenueVsBudget > 0 ? '+' : ''}${revenueVsBudget}%) and EBITDA had a ${ebitdaVsBudget > 0 ? '+' : ''}${ebitdaVsBudget}% deviation. The explanation should be sought in the combination of volume, price, structure absorption and cost intensity below operating margin.`}
            </p>
          </div>
        </div>
      </ExecutiveSummarySection>

      {/* ============ WATERFALL CHART ============ */}
      <div 
        className="rounded-[16px] overflow-hidden"
        style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
      >
        <div 
          className="px-8 py-5 border-b"
          style={{ borderColor: colors.border.soft }}
        >
          <h3 className={typography.sectionTitle} style={{ color: colors.text.primary }}>
            {language === 'pt' ? 'Formação do Resultado' : 'Result Formation'}
          </h3>
          <p className={typography.smallText} style={{ color: colors.text.tertiary, marginTop: '4px' }}>
            {language === 'pt' ? 'Receitas → Custos por Rubrica → EBITDA → Resultado Líquido (M USD)' : 'Revenue → Costs by Item → EBITDA → Net Income (M USD)'}
          </p>
        </div>
        <div className="px-6 py-6">
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={waterfallBars} margin={{ top: 30, right: 20, left: 20, bottom: 50 }} barCategoryGap="8%">
              <CartesianGrid strokeDasharray="3 3" stroke={colors.border.soft} vertical={false} />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 10, fontWeight: 500, fill: colors.text.tertiary }} 
                stroke={colors.border.soft}
                angle={-35}
                textAnchor="end"
                height={60}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 10, fill: colors.text.tertiary }} 
                stroke={colors.border.soft}
                domain={[0, 320]}
                tickFormatter={(v) => `${v}M`}
              />
              <Tooltip 
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload;
                    return (
                      <div 
                        className="rounded-lg px-4 py-3 shadow-xl"
                        style={{ backgroundColor: colors.bg.dark }}
                      >
                        <p className="font-semibold text-[13px] mb-1" style={{ color: colors.accent.gold }}>{data.name}</p>
                        <p className="text-sm" style={{ 
                          color: data.isNegative ? colors.status.negative : 
                                 (data.isTotal || data.isSubtotal) ? colors.status.positive : 
                                 colors.text.inverse 
                        }}>
                          {data.displayValue > 0 && !data.isNegative ? '+' : ''}{formatM(data.displayValue)}M USD
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Bar dataKey="start" stackId="stack" fill="transparent" />
              <Bar dataKey={(d) => Math.abs(d.end - d.start)} stackId="stack" radius={[4, 4, 0, 0]}>
                {waterfallBars.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={
                      entry.isTotal ? colors.chart.netIncome
                      : entry.isSubtotal ? colors.chart.ebitda
                      : entry.isNegative ? colors.chart.cost
                      : entry.isStart ? colors.chart.revenue
                      : colors.chart.netIncome
                    }
                  />
                ))}
                <LabelList 
                  dataKey="displayValue"
                  position="top"
                  fill={colors.text.secondary}
                  fontSize={9}
                  fontWeight={600}
                  formatter={(value) => `${formatM(value)}M`}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex justify-center gap-6 mt-3">
            {[
              { color: colors.chart.revenue, label: language === 'pt' ? 'Receitas' : 'Revenue' },
              { color: colors.chart.cost, label: language === 'pt' ? 'Custos' : 'Costs' },
              { color: colors.chart.ebitda, label: 'EBITDA' },
              { color: colors.chart.netIncome, label: language === 'pt' ? 'Res. Líquido' : 'Net Income' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: item.color }}></div>
                <span className={typography.smallText} style={{ color: colors.text.secondary }}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ============ DEVIATION PANEL ============ */}
      <div 
        className="rounded-[16px] overflow-hidden"
        style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
      >
        <div 
          className="px-8 py-5 border-b sticky top-0 z-10"
          style={{ borderColor: colors.border.soft, backgroundColor: colors.bg.card }}
        >
          <h3 className={typography.sectionTitle} style={{ color: colors.text.primary }}>
            {language === 'pt' ? 'Painel de Desvios' : 'Deviation Panel'}
          </h3>
          <p className={typography.smallText} style={{ color: colors.text.tertiary, marginTop: '4px' }}>
            {language === 'pt' ? 'Principais linhas da DR vs Orçamento e período homólogo' : 'Key P&L lines vs Budget and prior period'}
          </p>
        </div>
        <div className="overflow-x-auto" style={{ maxHeight: '350px', overflowY: 'auto' }}>
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr style={{ backgroundColor: colors.bg.dark }}>
                <th className="text-left py-4 px-6 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[20%]">
                  {language === 'pt' ? 'Rubrica' : 'Item'}
                </th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[12%]">Real {currentPeriod}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[12%]">{year === 2026 ? 'Orç. 1T26' : `Orç. ${year}`}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider w-[10%]" style={{ color: colors.accent.gold }}>Var. Abs.</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider w-[10%]" style={{ color: colors.accent.gold }}>Var. %</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/50 w-[12%]">{previousPeriod}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/50 w-[10%]">{year === 2026 ? `Var. % vs ${previousPeriod}` : 'Var. % YoY'}</th>
              </tr>
            </thead>
            <tbody>
              {deviationData.map((row, idx) => {
                const varAbs = row.real2025 - row.budget2025;
                const varPct = row.isPercent 
                  ? (row.real2025 - row.budget2025).toFixed(1)
                  : ((row.real2025 - row.budget2025) / Math.abs(row.budget2025) * 100).toFixed(1);
                const varYoY = row.isPercent 
                  ? (row.real2025 - row.real2024).toFixed(1)
                  : ((row.real2025 - row.real2024) / Math.abs(row.real2024) * 100).toFixed(1);
                
                const isGoodVsBudget = row.inverse ? parseFloat(varPct) <= 0 : parseFloat(varPct) >= 0;
                const isGoodYoY = row.inverse ? parseFloat(varYoY) <= 0 : parseFloat(varYoY) >= 0;

                return (
                  <tr 
                    key={idx} 
                    className="border-b transition-colors hover:bg-slate-50/50"
                    style={{ 
                      borderColor: colors.border.soft,
                      backgroundColor: row.highlight ? colors.accent.goldBg : 'transparent'
                    }}
                  >
                    <td 
                      className="py-4 px-6 text-[13px]"
                      style={{ color: colors.text.primary, fontWeight: row.highlight ? 600 : 400 }}
                    >
                      {row.label[language]}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-[13px] font-medium" style={{ color: colors.text.primary }}>
                      {row.isPercent ? `${row.real2025}%` : `${formatM(row.real2025)}M`}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-[13px]" style={{ color: colors.text.tertiary }}>
                      {row.isPercent ? `${row.budget2025}%` : `${formatM(row.budget2025)}M`}
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-[13px]" style={{ color: isGoodVsBudget ? colors.status.positive : colors.status.negative }}>
                      {varAbs >= 0 ? '+' : ''}{row.isPercent ? `${varAbs.toFixed(1)}p.p.` : `${formatM(varAbs)}M`}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span 
                        className="inline-flex px-2 py-0.5 rounded text-[11px] font-semibold"
                        style={{ backgroundColor: isGoodVsBudget ? colors.status.positiveBg : colors.status.negativeBg, color: isGoodVsBudget ? colors.status.positive : colors.status.negative }}
                      >
                        {parseFloat(varPct) >= 0 ? '+' : ''}{varPct}{row.isPercent ? 'p.p.' : '%'}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-right tabular-nums text-[13px]" style={{ color: colors.text.tertiary }}>
                      {row.isPercent ? `${row.real2024}%` : `${formatM(row.real2024)}M`}
                    </td>
                    <td className="py-4 px-4 text-right">
                      <span className="text-[12px] font-medium" style={{ color: isGoodYoY ? colors.status.positive : colors.status.negative }}>
                        {parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}{row.isPercent ? 'p.p.' : '%'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ============ FULL INCOME STATEMENT ============ */}
      <div 
        className="rounded-[16px] overflow-hidden"
        style={{ backgroundColor: colors.bg.card, border: `1px solid ${colors.border.default}` }}
      >
        <div 
          className="px-8 py-5 border-b sticky top-0 z-10"
          style={{ borderColor: colors.border.soft, backgroundColor: colors.bg.card }}
        >
          <h3 className={typography.sectionTitle} style={{ color: colors.text.primary }}>
            {language === 'pt' ? 'Demonstração de Resultados' : 'Income Statement'}
          </h3>
        </div>
        <div className="overflow-x-auto" style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <table className="w-full">
            <thead className="sticky top-0 z-10">
              <tr style={{ backgroundColor: colors.bg.dark }}>
                <th className="text-left py-4 px-6 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[26%]">
                  {language === 'pt' ? 'Descrição' : 'Description'}
                </th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[11%]">{currentPeriod}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/80 w-[11%]">{year === 2026 ? 'Orç. 1T26' : `Orç. ${year}`}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider w-[10%]" style={{ color: colors.accent.gold }}>Var. Abs.</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider w-[10%]" style={{ color: colors.accent.gold }}>Var. %</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/50 w-[11%]">{previousPeriod}</th>
                <th className="text-right py-4 px-4 text-[11px] font-semibold uppercase tracking-wider text-white/50 w-[10%]">{year === 2026 ? `Var. % vs ${previousPeriod}` : 'Var. % YoY'}</th>
              </tr>
            </thead>
            <tbody>
              {incomeStatementRows.map((row, idx) => {
                if (row.type === 'header') {
                  return (
                    <tr key={idx} style={{ backgroundColor: colors.bg.surfaceAlt }}>
                      <td colSpan={7} className="py-3 px-6 font-semibold text-[12px]" style={{ color: colors.text.primary }}>
                        {row.label[language]}
                      </td>
                    </tr>
                  );
                }
                
                const varAbs = (row.current || 0) - (row.budget || 0);
                const varPct = row.budget ? ((row.current - row.budget) / Math.abs(row.budget) * 100).toFixed(1) : 0;
                const varYoY = row.prior ? ((row.current - row.prior) / Math.abs(row.prior) * 100).toFixed(1) : 0;
                const isGoodVsBudget = row.isNegative ? parseFloat(varPct) <= 0 : parseFloat(varPct) >= 0;
                const isGoodYoY = row.isNegative ? parseFloat(varYoY) <= 0 : parseFloat(varYoY) >= 0;

                return (
                  <tr 
                    key={idx} 
                    className="border-b transition-colors"
                    style={{ 
                      borderColor: colors.border.soft,
                      backgroundColor: row.type === 'highlight' ? colors.accent.goldBg : 
                                       row.type === 'subtotal' ? colors.bg.surfaceAlt : 'transparent'
                    }}
                  >
                    <td 
                      className={`py-3 px-6 text-[13px] ${row.type === 'row' ? 'pl-10' : ''}`}
                      style={{ 
                        color: row.type === 'row' ? colors.text.secondary : colors.text.primary,
                        fontWeight: row.type === 'highlight' ? 600 : row.type === 'subtotal' ? 500 : 400,
                        fontStyle: row.type === 'margin' ? 'italic' : 'normal'
                      }}
                    >
                      {row.label[language]}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-[13px] font-medium" style={{ color: colors.text.primary }}>
                      {row.isPercent ? `${row.current}%` : `${row.isNegative ? '-' : ''}${formatM(Math.abs(row.current))}M`}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-[13px]" style={{ color: colors.text.tertiary }}>
                      {row.isPercent ? `${row.budget}%` : `${row.isNegative ? '-' : ''}${formatM(Math.abs(row.budget))}M`}
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-[13px]" style={{ color: isGoodVsBudget ? colors.status.positive : colors.status.negative }}>
                      {varAbs >= 0 ? '+' : ''}{formatM(varAbs)}M
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span 
                        className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold"
                        style={{ backgroundColor: isGoodVsBudget ? colors.status.positiveBg : colors.status.negativeBg, color: isGoodVsBudget ? colors.status.positive : colors.status.negative }}
                      >
                        {parseFloat(varPct) >= 0 ? '+' : ''}{varPct}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right tabular-nums text-[13px]" style={{ color: colors.text.tertiary }}>
                      {row.isPercent ? `${row.prior}%` : `${row.isNegative ? '-' : ''}${formatM(Math.abs(row.prior))}M`}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-[11px] font-medium" style={{ color: isGoodYoY ? colors.status.positive : colors.status.negative }}>
                        {parseFloat(varYoY) >= 0 ? '+' : ''}{varYoY}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default IncomeStatementMain;
